import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tags, Plus, Check } from 'lucide-react';
import { useQuoteStore } from '../store/quote-store';
import type { QuoteTag } from '../types/quote.types';

const TAG_COLORS = [
  '#6366f1',
  '#8b5cf6',
  '#ec4899',
  '#ef4444',
  '#f59e0b',
  '#10b981',
  '#3b82f6',
  '#06b6d4',
];

interface TagSelectorProps {
  quoteId: string;
  selectedTags: QuoteTag[];
  onClose?: () => void;
}

export function TagSelector({ quoteId, selectedTags, onClose }: TagSelectorProps) {
  const { tags, fetchTags, createTag, setQuoteTags } = useQuoteStore();
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0]);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(selectedTags.map((t) => t.id))
  );

  useEffect(() => {
    if (tags.length === 0) fetchTags();
  }, []);

  const toggleTag = (tagId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) {
        next.delete(tagId);
      } else {
        next.add(tagId);
      }
      return next;
    });
  };

  const handleApply = async () => {
    await setQuoteTags(quoteId, Array.from(selectedIds));
    onClose?.();
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    try {
      setIsCreating(true);
      const tag = await createTag(newTagName.trim(), newTagColor);
      setSelectedIds((prev) => new Set(prev).add(tag.id));
      setNewTagName('');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="w-72 p-3 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Tags className="w-4 h-4" />
        Etiquetas
      </div>

      {/* Existing tags */}
      <div className="max-h-48 overflow-y-auto space-y-1">
        {tags.map((tag) => (
          <button
            key={tag.id}
            onClick={() => toggleTag(tag.id)}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent text-sm text-left"
          >
            <span
              className="w-3 h-3 rounded-full shrink-0"
              style={{ backgroundColor: tag.color }}
            />
            <span className="flex-1 truncate">{tag.name}</span>
            {selectedIds.has(tag.id) && <Check className="w-4 h-4 text-primary shrink-0" />}
          </button>
        ))}
        {tags.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-2">
            No hay etiquetas. Crea una nueva.
          </p>
        )}
      </div>

      {/* Create new tag */}
      <div className="border-t pt-2 space-y-2">
        <div className="flex gap-1">
          <Input
            placeholder="Nueva etiqueta..."
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            className="h-8 text-sm"
            onKeyDown={(e) => e.key === 'Enter' && handleCreateTag()}
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={handleCreateTag}
            disabled={!newTagName.trim() || isCreating}
            className="h-8 w-8 p-0"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex gap-1">
          {TAG_COLORS.map((color) => (
            <button
              key={color}
              onClick={() => setNewTagColor(color)}
              className={`w-5 h-5 rounded-full border-2 ${
                newTagColor === color ? 'border-foreground' : 'border-transparent'
              }`}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      </div>

      {/* Apply button */}
      <Button size="sm" className="w-full" onClick={handleApply}>
        Aplicar
      </Button>
    </div>
  );
}
